import React from 'react';
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from 'lucide-react';
import "../styles/legalDocs.css";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <>
      <div className="top-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-8 h-8" />
        </button>
        <h1
          className="top-title"
          style={{ fontFamily: "unset", fontSize: "21px" }}
        >
          Privacy Policy
        </h1>
      </div>

      <div className="legal-docs">
        {/* <h1>Privacy Policy</h1> */}
        <p><strong>Effective Date:</strong> 24/03/2025</p>

        <p>Welcome to Socius Apps Ltd ("Company," "we," "our," or "us"). We value your privacy and are committed to protecting your personal data in compliance with applicable global data protection laws, including but not limited to:</p>

        <ul>
          <li><strong>General Data Protection Regulation (GDPR)</strong> -- for users in the European Economic Area (EEA)</li>
          <li><strong>California Consumer Privacy Act (CCPA)</strong> -- for users in California, USA</li>
          <li><strong>Children's Online Privacy Protection Act (COPPA)</strong> -- for users under the age of 18 in the USA</li>
          <li><strong>Other relevant privacy laws applicable to our global users</strong></li>
        </ul>

        <p>This Privacy Policy explains how we collect, use, store, share, and protect your personal data when you use our mobile application (the "App"). By using the App, you agree to the terms of this Privacy Policy.</p>

        <h2>1. DATA CONTROLLER AND CONTACT INFORMATION</h2>
        <p>The data controller responsible for processing personal data is:</p>
        <ul>
          <li><strong>Company Name:</strong> Socius Apps Ltd</li>
          <li><strong>Company Address:</strong> Megalou Alexandrou, 38, 2682, Paliometoxo, Nicosia, Cyprus</li>
          <li><strong>Email:</strong> <a href="mailto:support@socius.icu">support@socius.icu</a></li>
        </ul>
        <p>For any questions regarding this Privacy Policy, please contact us at the email provided above.</p>

        <h2>2. INFORMATION WE COLLECT</h2>
        <p>We collect different types of personal data depending on how you use our App. This includes:</p>

        <h3>2.1 Personal Information You Provide</h3>
        <p>When you register or use our services, you may provide:</p>
        <ol>
          <li><strong>Account Information:</strong> Name, email, phone number, username, password</li>
          <li><strong>Profile Information:</strong> Profile picture, bio, social media links</li>
          <li><strong>Content You Share:</strong> Photos, videos, comments, likes, captions, and associated location data</li>
        </ol>

        <h3>2.2 Information We Collect Automatically</h3>
        <p>We collect certain data automatically when you use our App, including but not limited to:</p>
        <ol>
          <li><strong>Device Information:</strong> IP address, device type, operating system, mobile network provider.</li>
          <li><strong>Usage Data:</strong> Features used, time spent on the App, interactions with other users.</li>
          <li><strong>Behavioral Data:</strong> Likes, comments, shares, and engagement metrics.</li>
          <li><strong>Location Data:</strong> Approximate or precise location based on GPS, Wi-Fi, or mobile network data, depending on your device settings and permissions.</li>
        </ol>

        <h3>2.3 Data from Device Permissions</h3>
        <p>With your explicit consent, we access:</p>
        <ol>
          <li><strong>Camera & Gallery:</strong> To upload photos and videos</li>
          <li><strong>Microphone:</strong> To record and share audio</li>
          <li><strong>Location Data:</strong> To tag locations in posts and personalize recommendations</li>
        </ol>
        <p>If you do not grant permission, some features of the App may be restricted.</p>

        <h2>3. LEGAL BASIS FOR PROCESSING (GDPR COMPLIANCE)</h2>
        <p>Under the General Data Protection Regulation (GDPR), we process your personal data based on the following legal grounds:</p>
        <ol>
          <li><strong>Consent:</strong> When you voluntarily provide personal data, grant permissions (e.g., location, camera, microphone access), or opt-in to marketing communications. You can withdraw your consent at any time through your account settings or by contacting us.</li>
          <li><strong>Contractual Necessity:</strong> When processing is required to provide our services, such as account registration, content sharing, enabling communication features, and maintaining your profile. Without this data, we may be unable to fulfill our contractual obligations.</li>
          <li><strong>Legitimate Interests:</strong> When processing is necessary for purposes such as improving user experience, analyzing app usage trends, ensuring security, preventing fraudulent activities, enforcing our terms of service, and protecting our users and platform. We always balance our interests with your rights and freedoms.</li>
          <li><strong>Legal Obligations:</strong> When we are required to process personal data to comply with laws, regulations, court orders, or law enforcement requests. This includes responding to legal claims and fulfilling data protection obligations.</li>
          <li><strong>Vital Interests:</strong> In rare cases, we may process data to protect someone's life or physical safety, such as in situations where there is a risk of harm or an urgent security threat.</li>
          <li><strong>Public Interest:</strong> If necessary for tasks carried out in the public interest or under official authority, such as complying with regulatory requirements imposed by government authorities.</li>
        </ol>
        <p>If you have any questions about how we process your data or wish to exercise your data protection rights, please contact us at <a href="mailto:support@socius.icu">support@socius.icu</a>.</p>

        <h2>4. HOW WE USE YOUR DATA</h2>
        <p>We use your data for the following purposes:</p>
        <ul>
          <li>To provide and personalize our services</li>
          <li>To enhance user experience and recommend relevant content</li>
          <li>To enable communication and social interactions between users</li>
          <li>To monitor and enforce community guidelines and prevent abusive content</li>
          <li>To show relevant advertisements via Facebook Ads and Google Ads</li>
          <li>To conduct analytics and improve our App's performance</li>
          <li>To comply with legal and regulatory requirements</li>
        </ul>

        <h2>5. DATA SHARING AND THIRD-PARTY SERVICES</h2>
        <p>We do not sell your personal data. However, we may share it with:</p>

        <h3>5.1 Service Providers</h3>
        <p>We work with third parties to operate our App, including:</p>
        <ol>
          <li><strong>Hosting Services:</strong> Google Cloud Services (data storage and processing)</li>
          <li><strong>Advertising Partners:</strong> Facebook Ads, Google Ads (targeted advertising)</li>
        </ol>

        <h3>5.2 Legal Authorities</h3>
        <p>We may disclose your data if required by law, court order, or to protect the rights, safety, or security of users or the Company. Additionally, we may share information with law enforcement, regulatory bodies, or other governmental agencies when necessary to investigate fraud, illegal activities, or violations of our Terms of Service.</p>

        <h2>6. USER RIGHTS AND CHOICES</h2>
        <p>Depending on your jurisdiction, you have the following rights over your personal data:</p>

        <h3>6.1 Rights Under GDPR (EEA Users)</h3>
        <ol>
          <li><strong>Right to Access:</strong> Request a copy of your data</li>
          <li><strong>Right to Rectification:</strong> Correct inaccurate information</li>
          <li><strong>Right to Erasure:</strong> Delete your data ("Right to be Forgotten")</li>
          <li><strong>Right to Restrict Processing:</strong> Limit how we process your data</li>
          <li><strong>Right to Object:</strong> Withdraw consent at any time</li>
          <li><strong>Right to Data Portability:</strong> Transfer your data to another service</li>
        </ol>

        <h3>6.2 Rights Under CCPA (California Users)</h3>
        <ol>
          <li><strong>Right to Know:</strong> What personal data we collect and why</li>
          <li><strong>Right to Delete:</strong> Request removal of your personal data</li>
          <li><strong>Right to Opt-Out:</strong> Prevent the sale or sharing of your data (we do not sell data)</li>
        </ol>
        <p>To exercise these rights, please contact <a href="mailto:support@socius.icu">support@socius.icu</a>.</p>

        <h2>7. ACCOUNT DELETION & DATA RETENTION</h2>
        <p>You may request to delete your account and all associated data at any time via:</p>
        <p><strong>Settings &gt; Delete Account</strong></p>
        <p>Upon deletion:</p>
        <ul>
          <li>Your personal data and content will be permanently removed</li>
          <li>Any data shared with third parties will be deleted within a reasonable timeframe</li>
          <li>We retain data for as long as necessary to comply with legal obligations, resolve disputes, and prevent fraud.</li>
        </ul>

        <h2>8. COMMUNITY GUIDELINES AND CONTENT MODERATION</h2>
        <p>Users must not post content containing:</p>
        <ul>
          <li>Hate speech, discrimination, or violence</li>
          <li>Drug-related content or illegal substances</li>
          <li>Nudity, pornography, or sexually explicit material</li>
          <li>Threats, harassment, or illegal activities</li>
        </ul>
        <p>We reserve the right to remove content or suspend accounts that violate these policies. Users can report violations using the Report System in the App.</p>

        <h2>9. DATA SECURITY MEASURES</h2>
        <p>We implement strict security measures to protect your personal data, including:</p>
        <ul>
          <li>Encryption of sensitive data</li>
          <li>Access Controls to prevent unauthorized access</li>
          <li>Regular Security Audits to detect and address vulnerabilities</li>
        </ul>
        <p>While we take all reasonable precautions, no online service is 100% secure. Please report any security concerns to <a href="mailto:support@socius.icu">support@socius.icu</a>.</p>

        <h2>10. CHILDREN'S PRIVACY (COPPA COMPLIANCE)</h2>
        <p>Our App is not intended for children under 13 years of age. If we learn that a child under 13 has provided personal data, we will delete it immediately.</p>
        <p>If you are a parent or guardian and believe your child has provided personal data, please contact <a href="mailto:support@socius.icu">support@socius.icu</a>.</p>

        <h2>11. INTERNATIONAL DATA TRANSFERS</h2>
        <p>As a company based in Cyprus, we process and store data using Google Cloud Services, which may involve transfers of data to servers in different countries.</p>
        <p>Where required, we apply safeguards such as:</p>
        <ul>
          <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
          <li>Encryption & Secure Transfers to ensure data protection</li>
          <li>Legally binding contracts with third-party providers to ensure compliance with data laws.</li>
        </ul>

        <h2>12. CHANGES TO THIS PRIVACY POLICY</h2>
        <p>We may update this Privacy Policy periodically. Changes will be communicated via:</p>
        <ul>
          <li><strong>In-App Notifications</strong></li>
          <li><strong>Email</strong></li>
        </ul>
        <p>Your continued use of the App after updates constitutes acceptance of the revised policy.</p>

        <h2>13. CONTACT US</h2>
        <p>For any inquiries regarding this Privacy Policy, please contact us at:</p>
        <ul>
          <li><strong>Company Name:</strong> Socius Apps Ltd</li>
          <li><strong>Company Address:</strong> Megalou Alexandrou, 38, 2682, Paliometoxo, Nicosia, Cyprus</li>
          <li><strong>Email:</strong> <a href="mailto:support@socius.icu">support@socius.icu</a></li>
        </ul>
      </div>
    </>
  );
};

export default PrivacyPolicy;